import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';

/**
 * Side modal with content projection and custom header action buttons
 *
 * ### Animation
 *
 * - To get smooth closing animations and prevent glitches, let this component
 * handle the `hideModal` event, instead of using synchronous `*ngIf` to hide
 * or display it.
 *
 * ### Projection Slots
 *
 * This componet can project two slots of content:
 *  - Default (no slot),
 *  - Right-corner to add custom header buttons (`right-corner`)
 *
 * You can project a custom element using `slot="right-corner"`
 * in the innerHTML of this component:
 *
 * ```html
 * <app-side-modal>
 *   <button slot="right-corner" (click)="handleClick()">
 *     Click me!
 *   </button>
 * </app-side-modal>
 * ```
 */
@Component({
  selector: 'app-side-modal',
  template: `
    <div *ngIf="isVisible" class="side-modal side-modal--overlay">
      <div
        class="side-modal__container side-modal__container--animated"
        [class.side-modal__container--animated-out]="hideAnimated"
      >
        <div class="side-modal__fixed-header">
          <button
            *ngIf="!hideReturnButton"
            class="side-modal__return"
            type="button"
            label="Close"
            (click)="handleGoBack()"
          >
            Regresar
          </button>
          <div class="side-modal__right-corner">
            <ng-content select="[slot=right-corner]"></ng-content>
          </div>
        </div>
        <div class="side-modal__detail">
          <ng-content></ng-content>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./side-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SideModalComponent {
  /**
   * Hides the left-corner button that contains "< Regresar"
   */
  @Input() hideReturnButton = false;

  /**
   * Emits when the closing-animation has ended
   */
  @Output() hideModal: EventEmitter<boolean> = new EventEmitter();

  /**
   * Emits when the left-corner "< Regresar" button gets clicked
   */
  @Output() goBack: EventEmitter<void> = new EventEmitter();

  /**
   * Marks the start/end of the "out" animattn
   */
  public hideAnimated = false;

  /**
   *  Flag for the only container in he modal
   */
  public isVisible = false;

  /**
   * Display the only container in the modal
   *
   * The modal can be rendered while being hidden.
   */
  public showModal() {
    this.isVisible = true;
  }

  public handleHide() {
    if (this.isVisible) {
      this.hideAnimated = true;
      setTimeout(() => {
        this.isVisible = false;
        this.hideModal.next(true);
        this.hideAnimated = false;
      }, 500);
    }
  }

  public handleGoBack() {
    this.goBack.next();
  }
}
